import { FC, useMemo } from 'react';
import { TeamMember } from '@demind-inc/core';
import { Typography } from '@mui/material';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import './TeamTasksKanbanView.scss';
import { TeamTaskBucket } from '../../../data-access';
import { TaskCheckBox } from '../../common/TaskCheckBox/TaskCheckBox';
import { TODO_INTEGRATION_ICONS } from '../../TodoIntegration/constants';
import TeamMemberAvatar from '../TeamMemberAvatar';
import TeamTaskAssigneeSelect from '../TeamTaskAssigneeSelect';
import { getTeamRoleLabel, TeamTFunction } from '../i18n';
import {
  TEAM_TASK_BUCKET_ORDER,
  TeamTaskGroup,
  TeamTaskWithOwner,
  TeamTasksGroupBy,
  buildTeamTaskGroups,
  getTeamTaskAssigneeUserId,
  getTeamTaskDueLabel,
  getTeamTaskForDisplay,
  getTeamTaskKey,
  isTeamBoardTask,
  normalizeTeamTaskProviderIconKey,
} from '../utils';

interface TeamTasksKanbanViewProps {
  tasks: TeamTaskWithOwner[];
  segment: 'team' | 'shared';
  groupBy: TeamTasksGroupBy;
  members: TeamMember[];
  currentUserId: string;
  isTaskStatusUpdating: boolean;
  isAssigningTask: boolean;
  onOpenTask: (taskItem: TeamTaskWithOwner) => void;
  onToggleTask: (taskItem: TeamTaskWithOwner, completed: boolean) => void;
  onAssignTask: (taskItem: TeamTaskWithOwner, assigneeUserId: string) => void;
  onCreateInGroup: (group: TeamTaskGroup, groupBy: TeamTasksGroupBy) => void;
}

interface TeamTaskKanbanColumn extends TeamTaskGroup {
  accentClassName?: string;
}

const BUCKET_CLASS_NAMES: Record<TeamTaskBucket, string> = {
  overdue: 'team-tasks-kanban-view__bucket-dot--overdue',
  today: 'team-tasks-kanban-view__bucket-dot--today',
  upcoming: 'team-tasks-kanban-view__bucket-dot--upcoming',
  done: 'team-tasks-kanban-view__bucket-dot--done',
};

const BUCKET_ACCENT_CLASS_NAMES: Record<TeamTaskBucket, string> = {
  overdue: 'team-tasks-kanban-view__column--overdue',
  today: 'team-tasks-kanban-view__column--today',
  upcoming: 'team-tasks-kanban-view__column--upcoming',
  done: 'team-tasks-kanban-view__column--done',
};

const getTaskProviderIcon = (appFrom?: string) => {
  const iconKey = normalizeTeamTaskProviderIconKey(appFrom);

  return iconKey ? TODO_INTEGRATION_ICONS[iconKey] : null;
};

const buildDueColumns = (tasks: TeamTaskWithOwner[], t: TeamTFunction): TeamTaskKanbanColumn[] => {
  return TEAM_TASK_BUCKET_ORDER.map((bucket) => {
    const items = tasks.filter((taskItem) => taskItem.bucket === bucket);

    return {
      key: bucket,
      label: t(`team.tasks.buckets.${bucket}`),
      bucket,
      items,
      accentClassName: BUCKET_ACCENT_CLASS_NAMES[bucket],
    };
  }).filter((column) => column.items.length > 0 || column.bucket !== 'done');
};

const buildKanbanColumns = (
  tasks: TeamTaskWithOwner[],
  groupBy: TeamTasksGroupBy,
  t: TeamTFunction
): TeamTaskKanbanColumn[] => {
  if (groupBy === 'due') {
    return buildDueColumns(tasks, t);
  }

  return buildTeamTaskGroups(tasks, groupBy, t).map((group) => ({
    ...group,
    accentClassName: groupBy === 'member' ? 'team-tasks-kanban-view__column--member' : undefined,
  }));
};

const TeamTasksKanbanAvatar: FC<{
  name: string;
  initials: string;
  photoUrl?: string;
}> = ({ name, initials, photoUrl }) => {
  return <TeamMemberAvatar name={name} initials={initials} photoUrl={photoUrl} size="small" />;
};

const TeamTasksKanbanCard: FC<{
  taskItem: TeamTaskWithOwner;
  segment: 'team' | 'shared';
  groupBy: TeamTasksGroupBy;
  members: TeamMember[];
  currentUserId: string;
  isTaskStatusUpdating: boolean;
  isAssigningTask: boolean;
  onOpenTask: (taskItem: TeamTaskWithOwner) => void;
  onToggleTask: (taskItem: TeamTaskWithOwner, completed: boolean) => void;
  onAssignTask: (taskItem: TeamTaskWithOwner, assigneeUserId: string) => void;
}> = ({
  taskItem,
  segment,
  groupBy,
  members,
  currentUserId,
  isTaskStatusUpdating,
  isAssigningTask,
  onOpenTask,
  onToggleTask,
  onAssignTask,
}) => {
  const { t, i18n } = useTranslation();
  const task = getTeamTaskForDisplay(taskItem, t);
  const isDone = taskItem.bucket === 'done' || !!task.completed;
  const isTeamTask = segment === 'team' && isTeamBoardTask(taskItem);
  const canComplete = taskItem.permissions.canComplete;
  const showOwner = groupBy !== 'member';
  const showBoard = groupBy !== 'board';
  const providerIcon = getTaskProviderIcon(task.appFrom);
  const dueLabel = getTeamTaskDueLabel(task, taskItem.bucket, t, i18n.language);
  const statusLabel = groupBy === 'due' ? dueLabel : t(`team.tasks.buckets.${taskItem.bucket}`);

  return (
    <article
      className={clsx(
        'team-tasks-kanban-view__card',
        canComplete && 'team-tasks-kanban-view__card--own',
        isTeamTask && 'team-tasks-kanban-view__card--team-task'
      )}
      onClick={() => onOpenTask(taskItem)}
    >
      <div className="team-tasks-kanban-view__card-meta">
        <span className="team-tasks-kanban-view__status-chip">
          <span
            className={clsx(
              'team-tasks-kanban-view__bucket-dot',
              BUCKET_CLASS_NAMES[taskItem.bucket]
            )}
            aria-hidden="true"
          />
          <Typography variant="subtitle2" component="span">
            {statusLabel}
          </Typography>
        </span>
        {showBoard && (
          <span className="team-tasks-kanban-view__board-chip">
            {providerIcon && (
              <img src={providerIcon} alt="" className="team-tasks-kanban-view__provider-icon" />
            )}
            <Typography variant="subtitle2" component="span">
              {taskItem.project.name || t('team.tasks.noBoard')}
            </Typography>
          </span>
        )}
      </div>

      <div className="team-tasks-kanban-view__card-main">
        {canComplete && (
          <span
            className="team-tasks-kanban-view__checkbox-wrap"
            onClick={(event) => event.stopPropagation()}
          >
            <TaskCheckBox
              isCompleted={isDone}
              disabled={isTaskStatusUpdating || (task.recurring?.isRecurring === true && isDone)}
              size={18}
              svgRatio={1}
              onChange={(completed) => onToggleTask(taskItem, completed)}
            />
          </span>
        )}
        <Typography
          variant="body2"
          component="h3"
          className={clsx(
            'team-tasks-kanban-view__card-title',
            isDone && 'team-tasks-kanban-view__card-title--done'
          )}
        >
          {task.name}
        </Typography>
      </div>

      <footer className="team-tasks-kanban-view__card-footer">
        <span className="team-tasks-kanban-view__card-footer-meta">
          {isTeamTask && showOwner ? (
            <TeamTaskAssigneeSelect
              members={members}
              currentUserId={currentUserId}
              value={getTeamTaskAssigneeUserId(taskItem)}
              disabled={isAssigningTask || !taskItem.permissions.canAssign}
              onChange={(assigneeUserId) => onAssignTask(taskItem, assigneeUserId)}
            />
          ) : (
            showOwner && (
              <span className="team-tasks-kanban-view__owner">
                <TeamTasksKanbanAvatar
                  name={taskItem.owner.name}
                  initials={taskItem.owner.initials}
                  photoUrl={taskItem.owner.photoUrl}
                />
                <Typography variant="subtitle2" component="span">
                  {taskItem.owner.isCurrentUser
                    ? t('team.common.you')
                    : taskItem.owner.name.replace(/\s*\(You\)\s*$/, '').split(/\s+/)[0]}
                </Typography>
              </span>
            )
          )}
          {groupBy !== 'due' && (
            <Typography
              variant="subtitle2"
              component="span"
              className="team-tasks-kanban-view__due-label"
            >
              {dueLabel}
            </Typography>
          )}
        </span>
        {!canComplete && (
          <Typography
            variant="subtitle2"
            component="span"
            className="team-tasks-kanban-view__view-only"
          >
            {t('team.common.viewOnly')}
          </Typography>
        )}
      </footer>
    </article>
  );
};

const TeamTasksKanbanColumn: FC<{
  column: TeamTaskKanbanColumn;
  segment: 'team' | 'shared';
  groupBy: TeamTasksGroupBy;
  members: TeamMember[];
  currentUserId: string;
  isTaskStatusUpdating: boolean;
  isAssigningTask: boolean;
  onOpenTask: (taskItem: TeamTaskWithOwner) => void;
  onToggleTask: (taskItem: TeamTaskWithOwner, completed: boolean) => void;
  onAssignTask: (taskItem: TeamTaskWithOwner, assigneeUserId: string) => void;
  onCreateInGroup: (group: TeamTaskGroup, groupBy: TeamTasksGroupBy) => void;
}> = ({
  column,
  segment,
  groupBy,
  members,
  currentUserId,
  isTaskStatusUpdating,
  isAssigningTask,
  onOpenTask,
  onToggleTask,
  onAssignTask,
  onCreateInGroup,
}) => {
  const { t } = useTranslation();
  const providerIcon = column.provider ? getTaskProviderIcon(column.provider) : null;

  return (
    <section className={clsx('team-tasks-kanban-view__column', column.accentClassName)}>
      <header className="team-tasks-kanban-view__column-header">
        {column.bucket && (
          <span
            className={clsx(
              'team-tasks-kanban-view__bucket-dot',
              BUCKET_CLASS_NAMES[column.bucket]
            )}
            aria-hidden="true"
          />
        )}
        {providerIcon && (
          <img src={providerIcon} alt="" className="team-tasks-kanban-view__column-provider-icon" />
        )}
        {column.owner && (
          <TeamTasksKanbanAvatar
            name={column.owner.name}
            initials={column.owner.initials}
            photoUrl={column.owner.photoUrl}
          />
        )}
        <div className="team-tasks-kanban-view__column-copy">
          <Typography
            variant="body2"
            component="h2"
            className="team-tasks-kanban-view__column-title"
          >
            {column.label}
          </Typography>
          {column.owner && (
            <Typography
              variant="subtitle2"
              component="p"
              className="team-tasks-kanban-view__column-subtitle"
            >
              {column.owner.role ? getTeamRoleLabel(t, column.owner.role) : t('team.roles.member')}
            </Typography>
          )}
        </div>
        <Typography variant="subtitle2" component="span" className="team-tasks-kanban-view__count">
          {column.items.length}
        </Typography>
        {segment === 'team' && (
          <button
            type="button"
            className="team-tasks-kanban-view__column-create-button"
            onClick={() => onCreateInGroup(column, groupBy)}
          >
            +
          </button>
        )}
      </header>

      <div className="team-tasks-kanban-view__cards">
        {column.items.length ? (
          column.items.map((taskItem, index) => (
            <TeamTasksKanbanCard
              key={getTeamTaskKey(taskItem, index)}
              taskItem={taskItem}
              segment={segment}
              groupBy={groupBy}
              members={members}
              currentUserId={currentUserId}
              isTaskStatusUpdating={isTaskStatusUpdating}
              isAssigningTask={isAssigningTask}
              onOpenTask={onOpenTask}
              onToggleTask={onToggleTask}
              onAssignTask={onAssignTask}
            />
          ))
        ) : (
          <Typography
            variant="subtitle2"
            component="p"
            className="team-tasks-kanban-view__empty-column"
          >
            {t('team.tasks.nothingHere')}
          </Typography>
        )}
      </div>
    </section>
  );
};

const TeamTasksKanbanView: FC<TeamTasksKanbanViewProps> = ({
  tasks,
  segment,
  groupBy,
  members,
  currentUserId,
  isTaskStatusUpdating,
  isAssigningTask,
  onOpenTask,
  onToggleTask,
  onAssignTask,
  onCreateInGroup,
}) => {
  const { t } = useTranslation();
  const columns = useMemo(() => buildKanbanColumns(tasks, groupBy, t), [groupBy, t, tasks]);
  const shouldScrollHorizontally = groupBy === 'board' || columns.length > 3;

  return (
    <div
      className={clsx(
        'team-tasks-kanban-view',
        shouldScrollHorizontally && 'team-tasks-kanban-view--scroll-x'
      )}
      aria-label={t('team.tasks.kanbanAria')}
    >
      <div className="team-tasks-kanban-view__track">
        {columns.map((column) => (
          <TeamTasksKanbanColumn
            key={column.key}
            column={column}
            segment={segment}
            groupBy={groupBy}
            members={members}
            currentUserId={currentUserId}
            isTaskStatusUpdating={isTaskStatusUpdating}
            isAssigningTask={isAssigningTask}
            onOpenTask={onOpenTask}
            onToggleTask={onToggleTask}
            onAssignTask={onAssignTask}
            onCreateInGroup={onCreateInGroup}
          />
        ))}
      </div>
    </div>
  );
};

export default TeamTasksKanbanView;
